















































import axios from 'axios';
import 'chartjs-adapter-luxon';
import ReportControls, { ReportSpecByWeek } from '@/components/ReportControls.vue';
import { Line as LineChart } from 'vue-chartjs/legacy';
import SvgIcon from '@jamescoyle/vue-icon';
import { mdiAutorenew } from '@mdi/js';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  registerables,
} from 'chart.js';
import Vue from 'vue';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  ...registerables,
);

export interface PalletVolumeData {
  data: [
    {
      dateEntered: Date;
      dailyJobs: number;
      dailyPallets: string;
      totalPallets: string;
      dailyRev: string;
      totalRev: string;
    },
  ];
  dailyPalletTarget: number;
  dailyRevenueTarget: number;
}

const now = new Date();

export default Vue.extend({
  name: 'DailyPalletVolume',
  components: {
    ReportControls,
    LineChart,
    SvgIcon,
  },
  props: {
    palletChartId: {
      type: String,
      default: 'pal-line-chart',
    },
    revChartId: {
      type: String,
      default: 'rev-line-chart',
    },
    pwInputChartId: {
      type: String,
      default: 'pw-line-chart',
    },
    pwCdChartId: {
      type: String,
      default: 'cd-line-chart',
    },
    datasetIdKey: {
      type: String,
      default: 'label',
    },
    width: {
      type: Number,
      default: 400,
    },
    height: {
      type: Number,
      default: 400,
    },
    cssClasses: {
      default: '',
      type: String,
    },
    styles: {
      type: Object,
      default: () => '',
    },
    plugins: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: true,
      controlsLoaded: false,
      spinPath: mdiAutorenew,
      spinSize: 48,
      baseline: 0 ,
      depot: 'all',
      from: null,
      to: null,
      spec: {
        dateStyle: "week",
        depot: 'all',
        accountCodes: [],
        from: {
          year: now.getFullYear(),
          week: 1,
        },
        to: {
          year: now.getFullYear(),
          week: 1,
        },
        weekday_from: 2,
        weekday_to: 6,
      } as ReportSpecByWeek,
      chartData: {
        labels: [] as string[],
        datasets: [
          {
            label: 'Pallets',
            data: [] as number[],
            borderColor: '#0B234E',
            backgroundColor: '#0B234E',
            pointStyle: 'circle',
            pointHitRadius: 12,
          },
          {
            label: 'Target',
            data: [] as number[],
            borderColor: '#D82400',
            backgroundColor: '#D82400',
            borderDash: [8, 8],
            pointStyle: 'circle',
            pointHitRadius: 12,
          },
        ],
      },
      revChartData: {
        labels: [] as string[],
        datasets: [
          {
            label: 'Revenue',
            data: [] as number[],
            borderColor: '#82BD53',
            backgroundColor: '#82BD53',
            pointStyle: 'circle',
            pointHitRadius: 12,
          },
          {
            label: 'Target',
            data: [] as number[],
            borderColor: '#D82400',
            backgroundColor: '#D82400',
            borderDash: [8, 8],
            pointStyle: 'circle',
            pointHitRadius: 12,
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            display: false,
          },
          legend: {
            display: true,
            labels: {
              boxWidth: 8,
              boxHeight: 8,
              usePointStyle: true,
              font: {
                size: 14,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: 'xy',
          },
        },
      },
    };
  },
  computed: {
    dataUrl(): string {
      const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';
      const { depot, from, to } = this.spec;
      return `${BASE_URL}/reports/daily-pallet-volume/${depot}/${from.year}/${from.week}/${to.year}/${to.week}/`;
    },
  },
  watch: {
    spec: {
      immediate: true,
      async handler() {
        if (!this.controlsLoaded) {
          return;
        }
        const response = await axios.get<PalletVolumeData>(this.dataUrl, { params: {
          account_codes: this.spec.accountCodes.flatMap((a: any) => a.codes) }});
        this.chartData.labels = response.data.data.map((item) => {
          const dateFormatted = new Date(item.dateEntered);
          const options = {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          };
          return dateFormatted.toLocaleString('en-GB', options);
        });

        this.chartData.datasets[0].data = response.data.data.map((item) => parseInt(item.totalPallets, 10));
        this.chartData.datasets[1].data = this.chartData.labels.map(() => {
          if (this.spec.accountCodes.length === 0) {
            return this.baseline += response.data.dailyPalletTarget;
          }
          return 0;
        });
        this.baseline = 0;

        this.revChartData.labels = response.data.data.map((item) => {
          const dateFormatted = new Date(item.dateEntered);
          const options = {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          };
          return dateFormatted.toLocaleString('en-GB', options);
        });
        this.revChartData.datasets[0].data = response.data.data.map((item) => parseInt(item.totalRev, 10));
        this.revChartData.datasets[1].data = this.revChartData.labels.map(() => {
          if (this.spec.accountCodes.length === 0) {
            return this.baseline += (response.data.dailyRevenueTarget);
          }
          return 0;
        });
        this.loading = false;
        this.baseline = 0;
      },
    },
  },
});

